// Shared helpers for the blocking-policy editor. These read the GET /parties/{id}
// blocks plus the parent contact form's `blocking.*` subtree and translate user edits
// into that form state — which ContactPageForm submits via buildBlocksPayload on save.
// There is no separate request path.

import { ChipType } from 'components/@extended/StatusChip';
import { useCallback, useMemo } from 'react';
import { Path, useFormContext, useWatch } from 'react-hook-form';
import { ComplianceBlockActiveRuleDto } from 'types/compliance';
import {
    BlockedActionTypeCode,
    BlockSourceType,
    ContactDto,
    ContactForm,
    PartyBlockDto,
    PartyBlockingFormState,
} from 'types/contacts';
import { findBlockRow, isRowEnforcing, isRowPlanned } from './BlockingPolicyReadonlyShared';

// ─── Intent overlay ─────────────────────────────────────────────────────────────
//
// Edits are tracked as a Record keyed by sourceType + axis (manual writes) and by
// blockId (override writes), derived from the parent contact form's `blocking` subtree.

export type ManualIntent = { kind: 'manual'; action: 'SET' | 'DELETE'; reason: string };
export type OverrideIntent = {
    kind: 'override';
    blockId: number;
    isIgnored: boolean;
    blockedFrom: string | null;
    reason: string;
};

export type IntentMap = {
    manual: Partial<Record<'CASES' | 'TIMECARDS', ManualIntent>>;
    overrides: Record<number, OverrideIntent>;
};

const emptyIntents = (): IntentMap => ({ manual: {}, overrides: {} });

// ─── Form-backed intent overlay ─────────────────────────────────────────────────
//
// Reads/writes the parent contact form's `blocking` subtree via react-hook-form, so
// edits flow into the form state that ContactPageForm already submits via
// buildBlocksPayload on save — no separate request path.

const manualKey = (axis: 'CASES' | 'TIMECARDS'): 'manualCases' | 'manualTimecards' =>
    axis === 'CASES' ? 'manualCases' : 'manualTimecards';

const buildIntentsFromForm = (contact: ContactDto, blocking: PartyBlockingFormState | undefined): IntentMap => {
    if (!blocking) return emptyIntents();
    const out = emptyIntents();
    const blocks = contact.blocks ?? [];

    for (const axis of ['CASES', 'TIMECARDS'] as const) {
        const serverRow = blocks.find((b) => b.sourceType === 'MANUAL' && b.blockedActionTypeCode === axis);
        const baseEnabled = !!serverRow && !serverRow.isIgnored;
        const formState = blocking[manualKey(axis)];
        const formEnabled = formState?.enabled ?? baseEnabled;
        if (formEnabled !== baseEnabled) {
            out.manual[axis] = {
                kind: 'manual',
                action: formEnabled ? 'SET' : 'DELETE',
                reason: formState?.changeReason ?? '',
            };
        }
    }

    const formOverrides = blocking.overrides ?? {};
    for (const row of blocks) {
        if (row.sourceType === 'MANUAL') continue;
        const formRow = formOverrides[`r${row.id}`];
        if (!formRow) continue;
        const isIgnoredChanged = formRow.isIgnored !== row.isIgnored;
        const blockedFromChanged = formRow.blockedFrom !== row.blockedFrom;
        if (isIgnoredChanged || blockedFromChanged) {
            out.overrides[row.id] = {
                kind: 'override',
                blockId: row.id,
                isIgnored: formRow.isIgnored,
                blockedFrom: formRow.blockedFrom,
                reason: formRow.changeReason ?? '',
            };
        }
    }

    return out;
};

export const useFormIntents = (contact: ContactDto) => {
    const { control, setValue } = useFormContext<ContactForm>();
    const blocking = useWatch({ control, name: 'blocking' }) as PartyBlockingFormState | undefined;
    const intents = useMemo(() => buildIntentsFromForm(contact, blocking), [contact, blocking]);

    const setManual = useCallback(
        (axis: 'CASES' | 'TIMECARDS', intent: ManualIntent | null) => {
            const key = manualKey(axis);
            const serverRow = (contact.blocks ?? []).find(
                (b) => b.sourceType === 'MANUAL' && b.blockedActionTypeCode === axis
            );
            const baseEnabled = !!serverRow && !serverRow.isIgnored;
            const desiredEnabled = intent === null ? baseEnabled : intent.action === 'SET';
            setValue(`blocking.${key}.enabled` as Path<ContactForm>, desiredEnabled, { shouldDirty: true });
            setValue(`blocking.${key}.changeReason` as Path<ContactForm>, intent?.reason ?? '', { shouldDirty: true });
        },
        [contact, setValue]
    );

    const setOverride = useCallback(
        (blockId: number, intent: OverrideIntent | null) => {
            const base = `blocking.overrides.r${blockId}` as const;
            const serverRow = (contact.blocks ?? []).find((b) => b.id === blockId);
            if (intent === null) {
                if (!serverRow) return;
                setValue(`${base}.isIgnored` as Path<ContactForm>, serverRow.isIgnored, { shouldDirty: true });
                setValue(`${base}.blockedFrom` as Path<ContactForm>, serverRow.blockedFrom, {
                    shouldDirty: true,
                });
                setValue(`${base}.changeReason` as Path<ContactForm>, '', { shouldDirty: true });
                return;
            }
            setValue(`${base}.isIgnored` as Path<ContactForm>, intent.isIgnored, { shouldDirty: true });
            setValue(`${base}.blockedFrom` as Path<ContactForm>, intent.blockedFrom, { shouldDirty: true });
            setValue(`${base}.changeReason` as Path<ContactForm>, intent.reason, { shouldDirty: true });
        },
        [contact, setValue]
    );

    return { intents, setManual, setOverride };
};

// ─── Effective state with overlay applied ──────────────────────────────────────

export interface EffectiveRow {
    row: PartyBlockDto | undefined;
    intent: OverrideIntent | undefined;
    isIgnored: boolean;
    blockedFrom: string | null;
    enforcing: boolean;
    planned: boolean;
    present: boolean;
    gracePeriodDays: number | null;
}

export const effectiveRowFor = (
    contact: ContactDto,
    source: BlockSourceType,
    axis: BlockedActionTypeCode,
    intents: IntentMap
): EffectiveRow => {
    const row = findBlockRow(contact.blocks, source, axis);
    const intent = row ? intents.overrides[row.id] : undefined;
    const isIgnored = intent ? intent.isIgnored : (row?.isIgnored ?? false);
    const blockedFrom = intent ? intent.blockedFrom : (row?.blockedFrom ?? null);
    const synthRow: PartyBlockDto | null = row ? { ...row, isIgnored, blockedFrom } : null;
    return {
        row,
        intent,
        isIgnored,
        blockedFrom,
        enforcing: synthRow ? isRowEnforcing(synthRow) : false,
        planned: synthRow ? isRowPlanned(synthRow) : false,
        present: !!row,
        gracePeriodDays: row?.gracePeriodDays ?? null,
    };
};

export const effectiveManualEnabled = (
    contact: ContactDto,
    axis: 'CASES' | 'TIMECARDS',
    intents: IntentMap
): { enabled: boolean; row: PartyBlockDto | undefined; intent: ManualIntent | undefined } => {
    const row = findBlockRow(contact.blocks, 'MANUAL', axis);
    const intent = intents.manual[axis];
    const baseEnabled = !!row && !row.isIgnored;
    if (!intent) return { enabled: baseEnabled, row, intent: undefined };
    return { enabled: intent.action === 'SET', row, intent };
};

// ─── Status decoration helpers ─────────────────────────────────────────────────
//
// Binary status mirrors the readonly sidebar (`isAddingNewCasesBlocked` is a
// boolean on the party DTO). Planned-future blocks count as "not blocked" for
// the summary chip; the per-source narrative below still tells the user when
// a planned block will start enforcing.

export type AxisStatus = 'blocked' | 'not-blocked';

export const axisStatusFromRows = (rows: EffectiveRow[], manualEnabled: boolean): AxisStatus => {
    if (manualEnabled) return 'blocked';
    if (rows.some((r) => r.enforcing)) return 'blocked';
    return 'not-blocked';
};

export const axisStatusChip = (status: AxisStatus): { label: string; type: ChipType } => {
    if (status === 'blocked') return { label: 'Blocked', type: 'error' };
    return { label: 'Not blocked', type: 'success' };
};

// ─── Compliance rules for an axis (full DTO, filtered by which axis they affect) ─

export const complianceRulesForAxis = (
    contact: ContactDto,
    axis: BlockedActionTypeCode
): ComplianceBlockActiveRuleDto[] => {
    const rules = contact.complianceBlockActiveRules ?? [];
    const filter = axis === 'CASES' ? 'blocksCases' : axis === 'TIMECARDS' ? 'blocksTimecards' : 'blocksAmlTimecards';
    return rules.filter((r) => r[filter]);
};

// ─── Proactive compliance exemption (no row yet) ──────────────────────────────
//
// Mirrors blocking.debtTimecardsExempt but per-axis. Lives in the same form subtree
// so edits flow through buildBlocksPayload on save — at save time a no-id COMPLIANCE
// write is emitted (analogous to the DEBT path) and the BE persists it as a proactive
// override.

export type ComplianceExemptKey =
    | 'complianceCasesExempt'
    | 'complianceTimecardsExempt'
    | 'complianceAmlTimecardsExempt';

export const complianceExemptKey = (axis: BlockedActionTypeCode): ComplianceExemptKey => {
    if (axis === 'CASES') return 'complianceCasesExempt';
    if (axis === 'TIMECARDS') return 'complianceTimecardsExempt';
    return 'complianceAmlTimecardsExempt';
};
